import { SelectOption, useNotify } from '@globaleris/ui.library'
import { Box, Breadcrumbs, Button, Typography } from '@material-ui/core'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { PriceComponentTypeModel } from '../../../api/Argentia/endpoints/priceComponents'
import {
  ProductPriceModel,
  ProductPriceModelPatch,
  ProductPriceModelPost
} from '../../../api/Argentia/endpoints/products/model/products'
import { EnumPriceComponentReference } from '../../../api/Argentia/enums'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { getPriceComponentTypes } from '../../../components/shared/priceComponentsTable/priceComponentsTable.service'
import { useConfirmationDialog } from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { mapPriceComponentTypesToSelectOptions } from '../products.mapper'
import { AddProductPriceForm } from '../products.Model'
import {
  createPriceComponentType,
  deleteProductPriceComponents,
  fetchProductPriceComponentsWithDummyExpression,
  updatePriceComponentType
} from '../products.service'
import { ProductPriceCard } from './components/productPriceCard/productPriceCard'

interface ProductPriceComponentPageProps {
  location?: {
    state: {
      productId: number
      commodity: number
      backPageTitle: string
      productTitle: string
    }
  }
}

export const ProductsPriceComponentsPage: React.FC<ProductPriceComponentPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'products'])
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, error } = useNotify()
  const history = useHistory()

  const [productPriceData, setProductPriceData] = useState<ProductPriceModel[]>([])
  const [rawPriceComponentTypes, setRawPriceComponentTypes] = useState<PriceComponentTypeModel[]>(
    []
  )
  const [priceComponentTypesOptions, setPriceComponentTypesOptions] = useState<SelectOption[]>([])
  const [priceComponentTypesContratoOptions, setPriceComponentTypesContratoOptions] = useState<
    SelectOption[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pctContratoOptionsIsLoading, setPctContratoOptionsIsLoading] = useState<boolean>(false)

  const productId = props.location?.state?.productId
  const commodity = props.location?.state?.commodity
  if (!productId || !commodity) {
    history.goBack()
    return <></>
  }

  const backPageTitle = props.location?.state?.backPageTitle
  const productTitle = props.location?.state?.productTitle

  // #region Handlers Starts

  // -------------------------   ProductPriceCard Handlers  --------------------------- //
  const handleAddPriceComponentType = async (formData: AddProductPriceForm) => {
    let request: ProductPriceModelPost = {
      type: formData.type,
      product: { id: productId },
      price: { id: formData.priceComponentTypeId },
      regenerate: formData.regenerate ?? false
    }

    formData.priceContractId && (request.priceContract = { id: formData.priceContractId })

    await createPriceComponentType(productId, formData.priceComponentTypeId, request).then(
      response => {
        if (response.success) {
          info(t('products:forms.productPrice.notifications.save.success'))
          getProductPriceComponentData()
        } else {
          error(t('products:forms.productPrice.notifications.save.error'))
        }
      }
    )
  }

  const handleEditPriceComponentType = async (formData: AddProductPriceForm) => {
    const request: ProductPriceModelPatch = {
      type: formData.type,
      regenerate: formData.regenerate ?? false,
      price: { id: formData.priceComponentTypeId },
      priceContract: formData.priceContractId ? { id: formData.priceContractId } : null
    }

    await updatePriceComponentType(formData.id!, request).then(response => {
      if (response.success) {
        info(t('products:forms.productPrice.notifications.update.success'))
        getProductPriceComponentData()
      } else {
        error(t('products:forms.productPrice.notifications.update.error'))
      }
    })
  }

  const handleDeletePriceComponentType = async selectedRow => {
    getConfirmation(ConfirmationDialogs.DELETE_PRODUCT_PRICE_COMPONENT).then(async () => {
      await deleteProductPriceComponents(selectedRow.id).then(response => {
        if (response.success) {
          getProductPriceComponentData()
        }
      })
      closeConfirmation()
    })
  }

  const handleFinalRelationClick = async () => {
    setPctContratoOptionsIsLoading(true)
    const responseData = await getPriceComponentTypesOptions(
      EnumPriceComponentReference.Contrato,
      commodity
    )
    if (responseData) {
      const filteredPriceComponentTypes = filterAlreadyUsePriceComponentTypes(
        responseData,
        EnumPriceComponentReference.Contrato
      )
      setPriceComponentTypesContratoOptions(
        mapPriceComponentTypesToSelectOptions(filteredPriceComponentTypes)
      )
      setPctContratoOptionsIsLoading(false)
    }
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const filterAlreadyUsePriceComponentTypes = (
    priceComponentTypes: PriceComponentTypeModel[],
    referenceType: EnumPriceComponentReference
  ): PriceComponentTypeModel[] => {
    return priceComponentTypes.filter(type => {
      return referenceType === EnumPriceComponentReference.Contrato
        ? !productPriceData.some(
            (productPrice: ProductPriceModel) => productPrice.priceContract?.id === type.id
          )
        : !productPriceData.some(
            (productPrice: ProductPriceModel) => productPrice.price.id === type.id
          )
    })
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  const getProductPriceComponentData = useCallback(async () => {
    setIsLoading(true)
    await fetchProductPriceComponentsWithDummyExpression(productId)
      .then(response => {
        if (response.success && response.data) {
          setProductPriceData(response.data)
        } else {
          setProductPriceData([])
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [productId])

  const getPriceComponentTypesOptions = useCallback(
    async (
      referenceType: EnumPriceComponentReference,
      commodity: number
    ): Promise<PriceComponentTypeModel[]> => {
      const expandDummy: boolean = false
      const response = await getPriceComponentTypes(
        referenceType,
        undefined,
        commodity,
        expandDummy
      )
      if (response.success && response.data) {
        return response.data
      } else return [] as PriceComponentTypeModel[]
    },
    []
  )

  useEffect(() => {
    getProductPriceComponentData()
    const fetchPriceComponentTypes = async () => {
      const responseData = await getPriceComponentTypesOptions(
        EnumPriceComponentReference.Oferta,
        commodity
      )
      if (responseData) {
        setRawPriceComponentTypes(responseData)
      }
    }
    fetchPriceComponentTypes()
  }, [getProductPriceComponentData, getPriceComponentTypesOptions, commodity])

  useEffect(() => {
    if (rawPriceComponentTypes.length > 0 && productPriceData !== undefined) {
      const filtered = filterAlreadyUsePriceComponentTypes(
        rawPriceComponentTypes,
        EnumPriceComponentReference.Oferta
      )
      setPriceComponentTypesOptions(mapPriceComponentTypesToSelectOptions(filtered))
    }
  }, [rawPriceComponentTypes, productPriceData])
  // #endregion Hooks Ends

  return (
    <Page>
      <PageHeader title={t('products:components.productPriceComponents.title')}>
        <Button color="primary" onClick={() => history.goBack()}>
          {t('products:mainActions.back')}
        </Button>
      </PageHeader>

      <Box ml={2} mb={1}>
        <Breadcrumbs separator=">" aria-label="breadcrumb">
          <Typography key="1" color="primary" variant="subtitle2">
            {backPageTitle}
          </Typography>
          <Typography key="2" color="primary" variant="subtitle1">
            {productTitle}
          </Typography>
        </Breadcrumbs>
      </Box>

      <ProductPriceCard
        key={priceComponentTypesOptions.length}
        data={productPriceData}
        commodity={commodity}
        isLoading={isLoading}
        priceComponentTypeOfertaOptions={priceComponentTypesOptions}
        priceComponentTypesContratoOptions={priceComponentTypesContratoOptions}
        pctContratoOptionsIsLoading={pctContratoOptionsIsLoading}
        onRowInsert={handleAddPriceComponentType}
        onRowEdit={handleEditPriceComponentType}
        onRowDelete={handleDeletePriceComponentType}
        onFinalRelationClick={handleFinalRelationClick}
      />
    </Page>
  )
}
